import { AgsCurrencyService } from "ags/services/ags-currency";
import { Layers } from "appworks/graphics/layers/layers";
import { LoaderService } from "appworks/loader/loader-service";
import { Model } from "appworks/model/model";
import { RealityMode, SettingsModel } from "appworks/model/settings/settings-model";
import { AbstractBetService } from "appworks/services/bet/abstract-bet-service";
import { CurrencyService } from "appworks/services/currency/currency-service";
import { Service } from "appworks/services/service";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { Contract } from "appworks/utils/contracts/contract";
import axios from "axios";
import * as queryString from "querystring";
import { slotModel } from "slotworks/model/slot-model";
import { SlotBetService } from "slotworks/services/bet/slot-bet-service";
import { BJBetService } from "tableworks/card/blackjack/services/bj-bet-service";
import { gameiomModel } from "./model/gameiom-model";
import { UIBetSettingsType } from "./wrapper/wrapper-data";

interface GameiomConfig {
    enableForcing: boolean;
    disclaimers: { [operatorId: string]: string };
}

class Gameiom {
    protected gameiomConfig: GameiomConfig;

    public init(): Contract<void> {
        return new Contract<void>((resolve) => {
            this.loadGameiomConfig().then(() => {
                gameWrapper.gameLoadingStarted();

                gameiomModel.enableForcing = this.gameiomConfig.enableForcing;
                gameiomModel.language = "en-GB";
                gameiomModel.currency = "GBP";
                // gameiomModel.denomDisplay = "denom";
                Model.write({ settings: { playMode: RealityMode.REAL } });

                this.readQueryStringParams();
                //this.setupPreloaderDisclaimer();
                this.setupStopOnFeatureJurisdiction();

                resolve(null);
            });
        });
    }

    public initWrapper(): void {
        Services.get(SoundService).onMuteChange.add(() => {
            if (Services.get(SoundService).getMuted()) {
                gameWrapper.disableAudio();
            } else {
                gameWrapper.enableAudio();
            }
        });
    }

    public setProviderId(id: number): void {
        gameiomModel.providerId = id;
    }

    public setupServices(): void {

        Services.get(CurrencyService).configure(gameiomModel.language, gameiomModel.currency, "symbol", true);
        AgsCurrencyService.CLEAN_FORMATTING = gameiomModel.shouldDisplayCredits();

        const settings = {
            displayCredits: !gameiomModel.displayCurrency,
            spaceToSpin: !gameiomModel.disableSpaceToSpin
        };

        Model.write({ settings });

        const betService = Services.get(AbstractBetService);
        if (betService) {
            betService.onBetChange.add(() => this.updateWrapperWithStake());
        }
    }

    public debugAuth(gameid: number = 1): Contract<void> {
        return new Contract<void>((resolve) => {
            gameiomModel.url = "wss://test-rgs.gameiom.com/platform";
            //gameiomModel.url = "wss://minikube.gameiom.com/platform";
            gameiomModel.username = new URLSearchParams(document.location.search).get("username") ?? "ags_test_29";
            //gameiomModel.username = "sadmin";
            gameiomModel.providerId = 48;
            gameiomModel.operatorId = 1;
            gameiomModel.gameId = gameid;
            // gameiomModel.jurisdiction = "uk";
            gameiomModel.currency = "GBP";
            //gameiomModel.currency = "TKN";
            //gameiomModel.isSocial = true;
            gameiomModel.disableSpinSkip = false;
            gameiomModel.slamStop = true;
            gameiomModel.displayCurrency = true;
            gameiomModel.betSettings = UIBetSettingsType.lines;

            // Testing operator params:
            // gameiomModel.denomDisplay = "denom";
            // gameiomModel.displayCurrency = true;
            // gameiomModel.displayWholeStakeAsDecimal = false;
            // gameiomModel.displayMaxBet = false;
            this.setupStopOnFeatureJurisdiction();

            let query = "https://test-rgs.gameiom.com/rgs/rest/authenticate/playerlogin";
            //let query = "https://minikube.gameiom.com/rgs/rest/authenticate/playerlogin";
            query += "?username=" + gameiomModel.username;
            query += "&password=" + gameiomModel.username;
            query += "&gameId=" + gameiomModel.gameId;
            query += "&operatorId=" + gameiomModel.operatorId;
            query += "&jurisdiction=" + gameiomModel.jurisdiction;

            axios.get(query, {
                headers: {
                    gameProvider: gameiomModel.providerId
                }
            }).then((response: any) => {
                const data = response.data;
                gameiomModel.sessionToken = data.token;

                resolve(null);
            });
        });
    }

    protected readQueryStringParams(): void {
        const params = queryString.parse(location.search.substring(1));
        const settings: Partial<SettingsModel> = {};

        if (params) {
            Object.keys(params).forEach((key: string) => {
                const value = params[key];

                if (key === "token") {
                    gameiomModel.sessionToken = value;
                } else if (key === "username") {
                    gameiomModel.username = value;
                } else if (key === "language") {
                    gameiomModel.language = value.split(" ").join("");
                    settings.language = value;
                } else if (key === "currency") {
                    gameiomModel.currency = value;
                } else if (key === "gameId") {
                    gameiomModel.gameId = parseInt(value);
                } else if (key === "operatorId") {
                    gameiomModel.operatorId = parseInt(value);
                } else if (key === "providerId") {
                    gameiomModel.providerId = parseInt(value);
                } else if (key === "home_url") {
                    settings.homeURL = value;
                } else if (key === "callbackUrl") {
                    gameiomModel.callbackUrl = value;
                } else if (key === "playMode") {
                    if (value === "real") {
                        settings.playMode = RealityMode.REAL;
                    } else {
                        settings.playMode = RealityMode.FREE;
                    }
                } else if (key === "jurisdiction") {
                    gameiomModel.jurisdiction = value;

                    /**
                     * AGS set all their jury options using the wrapper and there's actually a race condition at
                     * startup between some of the auth request & operator options making using the defaults fairly
                     * tricky to orchestrate.
                     */
                    // Services.jurisdiction.setJurisdiction(gameiomJurisdiction.get(value));
                } else if (key === "displayType" && gameiomModel.displayCurrency == null) {
                    // We only set this if it doesn't already have a value.
                    gameiomModel.displayCurrency = value === "currency";
                } else if (key === "platformServer") {
                    gameiomModel.url = value;
                }
            });
        }

        Model.write({ settings });
    }

    protected loadGameiomConfig(): Contract<string> {
        const query = "gameiom-config.json";

        return new Contract<string>((resolve) => {
            axios.get(query).then((response: any) => {
                this.gameiomConfig = response.data as GameiomConfig;
                resolve();
            });
        });
    }

    protected updateWrapperWithStake(): void {
        const betService = Services.get(AbstractBetService);
        gameWrapper.stakeUpdate(betService.getTotalStake());
    }

    protected setupPreloaderDisclaimer(): void {
        const menusLayer: Layers = Layers.get("Menus");

        for (const jurisdiction in this.gameiomConfig.disclaimers) {
            if (this.gameiomConfig.disclaimers.hasOwnProperty(jurisdiction)) {
                if (gameiomModel.jurisdiction && gameiomModel.jurisdiction.toString() === jurisdiction) {
                    Services.get(LoaderService).minPreloaderTime = 5000;
                    menusLayer.onSceneEnter.add((sceneName: string) => {
                        if (sceneName === "preloader") {
                            const disclaimer = menusLayer.getText("disclaimer");
                            if (disclaimer) {
                                disclaimer.text = this.gameiomConfig.disclaimers[jurisdiction];
                            }
                        }
                    });
                    return;
                }
            }
        }

        // Operators without a disclaimer should hide the text
        menusLayer.onSceneEnter.add((sceneName: string) => {
            if (sceneName === "preloader") {
                const disclaimer = menusLayer.getText("disclaimer");
                if (disclaimer) {
                    disclaimer.visible = false;
                }
            }
        });
    }

    /**
     * Stop on feature win should always be on in UK jurisdiction, unless ags send `stopOnFeatureWin = 'enabled'` as
     * part of their operator config.
     */
    protected setupStopOnFeatureJurisdiction() {

        if (gameiomModel.autoplayStopOnFeatureOverideSet) {
            return;
        }

        const stopOnFeatureWin = false;

        slotModel.write({ autoplay: { stopOnFeatureWin } });
    }
}

export const gameiom = new Gameiom();
